import { useState } from "react";
import * as Icons from "lucide-react";

/**
 * Embed-widget snippet generator. Realtor copies an `<iframe>` and pastes it
 * onto their own website / Squarespace / Wix page. Pure frontend — the only
 * requirement is that the underlying SharePage / RealtorDashboard work
 * inside an iframe (they already do because they're public, no auth, no
 * cross-site cookies).
 *
 * Usage:
 *   <EmbedSnippet kind="dashboard" url={dashboardUrl} title="Realtor Portfolio" />
 *   <EmbedSnippet kind="reel"      url={reelUrl}      title="Listing Reel" />
 */
export default function EmbedSnippet({ kind, url, title, brand }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [width, setWidth] = useState("100%");
  const [height, setHeight] = useState(kind === "reel" ? "1100" : "1600");

  if (!url) return null;

  const snippet = `<iframe
  src="${url}"
  title="${(title || "Property Motion AI").replace(/"/g, "&quot;")}"
  width="${width}"
  height="${height}"
  style="border:0;border-radius:16px;overflow:hidden;"
  loading="lazy"
  allowfullscreen
></iframe>`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt("Copy this snippet:", snippet);
    }
  };

  return (
    <div className="relative" data-testid="embed-snippet">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border bg-white/[0.04] border-white/10 text-zinc-300 hover:bg-white/[0.08] hover:border-white/30 text-xs transition-all"
        style={brand ? { borderColor: `${brand}40` } : undefined}
        data-testid="embed-snippet-trigger"
      >
        <Icons.Code2 size={12} />
        Embed
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[420px] z-50 bg-zinc-950 border border-white/10 rounded-xl shadow-2xl p-4 text-[12px]"
          data-testid="embed-snippet-panel"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-sm text-white">Embed snippet</div>
            <button
              onClick={() => setOpen(false)}
              className="text-zinc-500 hover:text-white"
            >
              <Icons.X size={14} />
            </button>
          </div>
          <p className="text-[11px] text-zinc-500 mb-3">
            Paste this into your website's HTML to embed the {kind === "reel" ? "reel" : "dashboard"} live —
            it stays in sync as you publish updates.
          </p>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">
              Width
              <input
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                className="mt-1 w-full bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white font-mono focus:outline-none focus:border-white/30"
                data-testid="embed-snippet-width"
              />
            </label>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">
              Height (px)
              <input
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                className="mt-1 w-full bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white font-mono focus:outline-none focus:border-white/30"
                data-testid="embed-snippet-height"
              />
            </label>
          </div>
          <pre
            className="bg-black/60 border border-white/10 rounded-md p-3 text-[10px] text-emerald-200 overflow-x-auto whitespace-pre-wrap break-all max-h-40 font-mono"
            data-testid="embed-snippet-code"
          >
            {snippet}
          </pre>
          <button
            onClick={copy}
            className="mt-3 w-full font-medium rounded-md py-2 inline-flex items-center justify-center gap-2 transition-all"
            style={{
              background: copied ? "#10b981" : brand || "#ffffff",
              color: copied ? "#ffffff" : brand ? "#ffffff" : "#000000",
            }}
            data-testid="embed-snippet-copy"
          >
            {copied ? (
              <>
                <Icons.Check size={13} />
                Copied to clipboard
              </>
            ) : (
              <>
                <Icons.Copy size={13} />
                Copy snippet
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
